/**
 * Tools tile: import competitor customers (SALES_INSIGHTS_PLAN.md §5b, M6).
 *
 * Takes a normalised CSV (or JSON array) — one row per company per competitor
 * per source list — dry-runs the match (securities spine, Cynapse name lookup,
 * headline embeddings), then replaces each source's rows on apply. The source
 * spreadsheets are principals-only: convert them to this shape off-platform.
 */
import { useState } from 'react';
import { Eye, Loader2, Play, Swords, Upload, XCircle } from 'lucide-react';
import { runCompetitorImport, type CompetitorImportResult, type CompetitorImportRow } from '../../lib/api/insights.js';

const COLUMNS = ['competitor', 'source', 'date_basis', 'last_seen', 'first_seen', 'name', 'ticker', 'pieces'] as const;

/** Minimal CSV parser: header row, quoted fields with "" escapes. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = []; let field = ''; let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') { row.push(field); field = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else field += ch;
  }
  row.push(field);
  if (row.some((c) => c.trim() !== '')) rows.push(row);
  return rows;
}

export function toImportRows(text: string): CompetitorImportRow[] {
  const t = text.trim();
  if (t.startsWith('[')) return JSON.parse(t) as CompetitorImportRow[];
  const [header, ...body] = parseCsv(t);
  const idx = Object.fromEntries(header.map((h, i) => [h.trim().toLowerCase(), i]));
  for (const c of ['competitor', 'source', 'date_basis', 'last_seen']) {
    if (!(c in idx)) throw new Error(`CSV needs a "${c}" column (columns: ${COLUMNS.join(', ')})`);
  }
  const get = (r: string[], c: string) => (c in idx ? (r[idx[c]] ?? '').trim() : '');
  return body.map((r) => ({
    competitor: get(r, 'competitor'), source: get(r, 'source'),
    date_basis: get(r, 'date_basis') as 'dated' | 'observed', last_seen: get(r, 'last_seen'),
    first_seen: get(r, 'first_seen') || null, name: get(r, 'name') || null, ticker: get(r, 'ticker') || null,
    pieces: get(r, 'pieces') ? Number(get(r, 'pieces')) : null,
  }));
}

export function CompetitorImportTile() {
  const [rows, setRows] = useState<CompetitorImportRow[] | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [floor, setFloor] = useState('');
  const [state, setState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [result, setResult] = useState<CompetitorImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dryRunOk, setDryRunOk] = useState(false);

  const load = async (f: File | undefined) => {
    setResult(null); setDryRunOk(false); setError(null); setState('idle');
    if (!f) return;
    try {
      setRows(toImportRows(await f.text())); setFileName(f.name);
    } catch (e: any) {
      setRows(null); setError(e?.message || 'Could not read the file'); setState('error');
    }
  };

  const run = async (apply: boolean) => {
    if (!rows) return;
    if (apply && !window.confirm(`Replace the competitor rows for ${[...new Set(rows.map((r) => r.source))].join(', ')} with this file's matches?`)) return;
    setState('running'); setError(null); setJobStatus(null);
    try {
      const out = await runCompetitorImport(rows, { dry_run: !apply, floor: floor ? Number(floor) : undefined },
        (s) => setJobStatus(s.status));
      setResult(out); setState('done'); setDryRunOk(!apply);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Request failed'); setState('error');
    }
  };

  return (
    <div className="bg-surface border border-grey-200 rounded-lg p-6 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5 text-grey-500"><Swords className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-grey-900">Competitor Customers Import</h3>
            <p className="text-sm text-grey-600 mt-1">
              Loads which companies buy coverage from a competitor, and when, for the hot/warm/cold tags on Insights.
              Upload a CSV with columns <code className="text-xs">{COLUMNS.join(', ')}</code> (date_basis is
              <code className="text-xs"> dated</code> or <code className="text-xs">observed</code>). Companies are matched by
              ticker, then name, then by comparing headlines with our own articles and filings; nothing is created from an
              unmatched name. Applying replaces each source's earlier rows.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-4 flex-shrink-0">
          <label className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 cursor-pointer">
            <Upload className="h-4 w-4" /> {fileName ?? 'Choose file'}
            <input type="file" accept=".csv,.json,text/csv,application/json" className="hidden" onChange={(e) => load(e.target.files?.[0])} />
          </label>
          <input value={floor} onChange={(e) => setFloor(e.target.value.replace(/[^\d.]/g, ''))} placeholder="floor 0.80"
            aria-label="Headline similarity floor" className="w-24 rounded-lg border border-grey-300 px-2 py-2 text-sm" />
          <button onClick={() => run(false)} disabled={!rows || state === 'running'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-grey-300 text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed">
            <Eye className="h-4 w-4" /> Dry run
          </button>
          <button onClick={() => run(true)} disabled={!rows || !dryRunOk || state === 'running'}
            title={dryRunOk ? '' : 'Dry-run this file first'}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
            <Play className="h-4 w-4" /> Apply
          </button>
        </div>
      </div>
      {rows && state === 'idle' && <p className="mt-3 text-xs text-grey-500">{rows.length} rows loaded from {fileName}.</p>}
      {state !== 'idle' && (
        <div className="mt-4 pt-4 border-t border-grey-100 space-y-2 text-sm">
          {state === 'running' && <div className="flex items-center gap-2 text-primary-600"><Loader2 className="h-4 w-4 animate-spin" /><span>{jobStatus === 'running' ? 'Matching…' : 'Queued as a background job…'}</span></div>}
          {state === 'error' && <div className="flex items-center gap-2 text-error-700"><XCircle className="h-4 w-4" /><span>{error}</span></div>}
          {state === 'done' && result && (
            <>
              <div className="text-grey-700">
                {result.matched_rows} of {result.rows} rows matched → <strong>{result.companies}</strong> companies, {result.signals} signals
                {result.dry_run ? ' (nothing changed)' : ` · ${result.inserted} written, ${result.deleted} replaced`}
              </div>
              <div className="text-xs text-grey-600">
                By method: {Object.entries(result.by_method).map(([k, v]) => `${k} ${v}`).join(' · ') || '—'}
                {result.name_lookup && result.name_lookup !== 'ok' && <> · Cynapse name lookup {result.name_lookup}</>}
                {' '}· headline floor {result.floor}
              </div>
              {result.headline_calibration && (
                <div className="text-xs text-grey-600">
                  Headline precision on {result.headline_calibration.sample} known matches:{' '}
                  {Object.entries(result.headline_calibration.by_floor)
                    .map(([f, c]) => `${f}: ${c.precision === null ? '—' : `${Math.round(c.precision * 100)}%`} (${c.agree}✓ ${c.wrong}✗)`).join(' · ')}
                </div>
              )}
              {result.headline_matches.length > 0 && (
                <details className="text-xs text-grey-600"><summary className="cursor-pointer">Headline matches ({result.headline_matches.length}) — review</summary>
                  <ul className="mt-1 space-y-0.5">{result.headline_matches.map((m, i) => <li key={i}>“{m.name}” → {m.company ?? '?'} <span className="text-grey-400">({m.score}, {m.votes} votes)</span></li>)}</ul>
                </details>
              )}
              {result.ambiguous.length > 0 && (
                <details className="text-xs text-grey-600"><summary className="cursor-pointer">Ambiguous ({result.ambiguous.length})</summary>
                  <ul className="mt-1 space-y-0.5">{result.ambiguous.map((a, i) => <li key={i}>{a.name} → {a.candidates.join(' / ')}</li>)}</ul>
                </details>
              )}
              {result.unmatched.length > 0 && (
                <details className="text-xs text-grey-600"><summary className="cursor-pointer">Unmatched ({result.unmatched.length})</summary>
                  <p className="mt-1">{result.unmatched.join(' · ')}</p>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
